import React, { useRef, useState } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import VaultRichEditor from './vault_rich_editor';

function makeStagedFile(file) {
  const name = file.name.replace(/\.[^/.]+$/, '');
  const extn = file.name.split('.').pop()?.toLowerCase() || '';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extn);
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    file,
    file_name: file.name,
    name,
    extn,
    previewUrl: isImage ? URL.createObjectURL(file) : null,
  };
}

function fallbackIcon(ext) {
  if (ext === 'pdf') return '📄';
  if (['doc', 'docx'].includes(ext)) return '📝';
  return '📎';
}

/**
 * Shared “New note” form: title, rich body, staged attachments.
 * Parent owns stagedFiles lifecycle (including URL.revokeObjectURL).
 */
const VaultCompose = ({
  title,
  body,
  onTitleChange,
  onBodyChange,
  stagedFiles,
  onStagedFilesChange,
  onCancel,
  onSubmit,
  isCreating,
  createError,
  canCreate,
  titleRef,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const addFiles = (fileList) => {
    const next = Array.from(fileList || []).map(makeStagedFile);
    if (!next.length) return;
    onStagedFilesChange([...stagedFiles, ...next]);
  };

  const removeStaged = (id) => {
    const target = stagedFiles.find((f) => f.id === id);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    onStagedFilesChange(stagedFiles.filter((f) => f.id !== id));
  };

  return (
    <section className="vault-compose fade-in">
      <header className="vault-compose-header">
        <h2>New note</h2>
        <button
          type="button"
          className="vault-icon-btn"
          title="Cancel"
          onClick={onCancel}
        >
          <FiX />
        </button>
      </header>

      <p className="vault-compose-hint">
        Nothing is saved until you add a title and create the note.
      </p>

      <input
        ref={titleRef}
        className="vault-detail-title-input vault-compose-title"
        placeholder="Title (required)"
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />

      <VaultRichEditor
        className="vault-compose-body"
        value={body}
        onChange={onBodyChange}
        placeholder="Optional details…"
      />

      <div
        className={`attachment-block vault-compose-attachments${dragOver ? ' dragover' : ''}`}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
      >
        <div className="attachment-block-header">
          <button
            type="button"
            className="attachment-add-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Add attachment"
          >
            <FiPlus />
          </button>
          <strong>Attachments</strong>
          {stagedFiles.length > 0 ? (
            <span className="vault-compose-attach-count">{stagedFiles.length}</span>
          ) : null}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {stagedFiles.length === 0 ? (
          <p className="vault-compose-attach-empty">
            Add photos or PDFs now — they’ll upload when you create the note.
          </p>
        ) : (
          <div className="attachment-grid">
            {stagedFiles.map((f) => (
              <div key={f.id} className="attachment-tile">
                {f.previewUrl ? (
                  <div className="attachment-thumb">
                    <img src={f.previewUrl} alt={f.name} />
                  </div>
                ) : (
                  <div className="attachment-icon">{fallbackIcon(f.extn)}</div>
                )}
                <div className="attachment-name" title={f.file_name}>
                  {f.name || f.file_name}
                </div>
                <button
                  type="button"
                  className="attachment-delete"
                  onClick={() => removeStaged(f.id)}
                  title="Remove"
                >
                  ✖
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {createError ? <div className="vault-compose-error">{createError}</div> : null}

      <div className="vault-compose-actions">
        <button
          type="button"
          className="vault-compose-cancel"
          onClick={onCancel}
          disabled={isCreating}
        >
          Cancel
        </button>
        <button
          type="button"
          className="vault-compose-submit"
          onClick={onSubmit}
          disabled={!canCreate}
        >
          {isCreating ? 'Creating…' : 'Create note'}
        </button>
      </div>
    </section>
  );
};

export default VaultCompose;
export { makeStagedFile };
